import { formatInTimeZone } from 'date-fns-tz';

import { type ApiQueryParamsType } from '@libraries/typescript/api_client';

export const OUTREACH_ANALYTICS_TIMEZONE = 'America/New_York';

// Auto-refresh cadence for the live dashboard sections.
export const OUTREACH_REFRESH_INTERVAL_MS = 60_000;

// Office-TV wait thresholds (seconds) that color the KPI, stat, and run badges
// green/amber/red. Calibrated to the current prod distribution (typical avg
// wait ~150s): under amber reads healthy, amber-to-red is a slowdown, past red
// is a problem visible from across the room.
export const TV_WAIT_AMBER_S = 180;
export const TV_WAIT_RED_S = 360;

// Time-to-first-staged-caregivers thresholds (seconds). Healthy post-prefetch
// avg is ~39s; a regression to pre-prefetch behavior (~54s) reads amber.
export const TV_STAGED_AMBER_S = 50;
export const TV_STAGED_RED_S = 90;

// Props of the tabs that follow the header window. The route owns the
// selection; each tab does its own data fetching.
export type OutreachTabProps = {
    readonly organizationIds: Array<string>;
    readonly window: OutreachWindowSelection;
    readonly urgency: OutreachUrgencyFilter;
};

export type OutreachUrgencyFilter = NonNullable<
    ApiQueryParamsType<'/admin/outreach-observability', 'get'>['urgency']
>;

// Preset window the Health and Auto-draft tabs can request. Must stay in sync
// with the backend OutreachLiveWindow in
// libraries/python/outreach_run_analytics; the hook's query param is
// type-checked against the generated client so a drift surfaces at compile
// time.
export type OutreachLiveWindow =
    | 'trailing_24h'
    | 'today'
    | 'trailing_3d'
    | 'trailing_7d'
    | 'trailing_14d';

// An explicit [start, end) range selected in the dashboard, as ISO instants.
// The API rejects ranges longer than MAX_OUTREACH_CUSTOM_RANGE_MS.
export type OutreachCustomRange = {
    readonly start: string;
    readonly end: string;
};

// The dashboard's window selection: a named preset or a custom range.
export type OutreachWindowSelection = OutreachLiveWindow | OutreachCustomRange;

// Longest custom range the dashboard may request. Must stay in sync with
// MAX_CUSTOM_WINDOW_DAYS in outreach_run_analytics.
export const MAX_OUTREACH_CUSTOM_RANGE_DAYS = 14;
export const MAX_OUTREACH_CUSTOM_RANGE_MS =
    MAX_OUTREACH_CUSTOM_RANGE_DAYS * 24 * 60 * 60 * 1000;

function isCustomOutreachRange(
    selection: OutreachWindowSelection,
): selection is OutreachCustomRange {
    return typeof selection !== 'string';
}

export const DEFAULT_OUTREACH_LIVE_WINDOW: OutreachLiveWindow = 'trailing_24h';

// `label` is the dropdown option text; `summary` is the compact phrase reused
// in inline copy ("Runs ({summary})").
export const OUTREACH_LIVE_WINDOW_OPTIONS: ReadonlyArray<{
    readonly value: OutreachLiveWindow;
    readonly label: string;
    readonly summary: string;
}> = [
    {
        value: 'trailing_24h',
        label: 'Trailing 24 hours',
        summary: 'trailing 24h',
    },
    { value: 'today', label: 'Today (since midnight ET)', summary: 'today' },
    {
        value: 'trailing_3d',
        label: 'Trailing 3 days',
        summary: 'trailing 3d',
    },
    {
        value: 'trailing_7d',
        label: 'Trailing 7 days',
        summary: 'trailing 7d',
    },
    {
        value: 'trailing_14d',
        label: 'Trailing 14 days',
        summary: 'trailing 14d',
    },
];

// Compact ET-labeled phrase for a custom range ("Jul 1, 9:00 AM – Jul 3,
// 5:00 PM ET"), reused by the summary below and the header inputs.
function formatOutreachCustomRange(range: OutreachCustomRange): string {
    const format = 'MMM d, h:mm a';
    const start = formatInTimeZone(
        new Date(range.start),
        OUTREACH_ANALYTICS_TIMEZONE,
        format,
    );
    const end = formatInTimeZone(
        new Date(range.end),
        OUTREACH_ANALYTICS_TIMEZONE,
        format,
    );
    return `${start} – ${end} ET`;
}

// Compact window phrase reused in inline copy across the tabs.
// Falls back to the raw value if an option is missing.
export function outreachWindowSummary(
    selection: OutreachWindowSelection,
): string {
    if (isCustomOutreachRange(selection)) {
        return formatOutreachCustomRange(selection);
    }
    return (
        OUTREACH_LIVE_WINDOW_OPTIONS.find(
            (option) => option.value === selection,
        )?.summary ?? selection
    );
}

// The window a dashboard response actually reflects — the echoed preset or
// echoed custom bounds — so labels and drill-downs follow the fetched data
// rather than an in-flight selector change. Null when the response predates
// the echo fields.
export function echoedOutreachWindow(response: {
    readonly window: OutreachLiveWindow | null;
    readonly window_start?: string | null;
    readonly window_end?: string | null;
}): OutreachWindowSelection | null {
    if (response.window) {
        return response.window;
    }
    if (response.window_start && response.window_end) {
        return { start: response.window_start, end: response.window_end };
    }
    return null;
}
